import { isManualUomSource, matchSageUnit, SageReconciliationMatch, UnitOfMeasureOption } from './sage-integration.model';
import { toUnitGroupView, UnitGroupView } from './sage-unit-group.model';
import { sageUnitOf, unitGroups } from './sage-wizard.model';

/** The console's units panel: the Sage items that didn't come into Aivor because Aivor doesn't have their unit. */
export interface UnitFixView {
  groups: UnitGroupView[];
  units: UnitOfMeasureOption[];
  /** Items waiting for a unit. */
  total: number;
  /** Items Apply would add: a unit saved, or a Sage unit Aivor now has. */
  applyCount: number;
  loading: boolean;
  loadError: boolean;
  /** Items whose unit is being saved; their group's pickers wait. */
  savingIds: ReadonlySet<number>;
  applying: boolean;
  error: string | null;
}

/**
 * The Items review rows refused for their unit. An import row with a unit chosen by hand, or one
 * that still matches nothing in Aivor, has its unit saved but hasn't been applied yet, so it is
 * listed too; a hand-picked unit Aivor already had would otherwise drop the row on "Use".
 */
export function unitFixMatches(matches: SageReconciliationMatch[], units: UnitOfMeasureOption[]): SageReconciliationMatch[] {
  return matches.filter(
    (m) =>
      m.decision === 'NeedsUomMapping' ||
      (m.decision === 'ImportToAivor' &&
        m.resolvedUOMID != null &&
        (isManualUomSource(m.uomResolutionSource) || !matchSageUnit(sageUnitOf(m), units)))
  );
}

/** The server matches a Sage unit Aivor now has by itself, so those need nothing saved first. */
export function unitFixApplyIds(matches: SageReconciliationMatch[], units: UnitOfMeasureOption[]): number[] {
  return unitFixMatches(matches, units)
    .filter((m) => m.resolvedUOMID != null || !!matchSageUnit(sageUnitOf(m), units))
    .map((m) => m.id);
}

export function unitFixView(
  matches: SageReconciliationMatch[],
  units: UnitOfMeasureOption[],
  reasons: ReadonlyMap<number, string>,
  savingIds: ReadonlySet<number> = new Set()
): UnitFixView {
  const waiting = unitFixMatches(matches, units);
  return {
    groups: unitGroups(waiting, units).map((g) => toUnitGroupView(g, units, reasons)),
    units,
    total: waiting.length,
    applyCount: unitFixApplyIds(matches, units).length,
    loading: false,
    loadError: false,
    savingIds,
    applying: false,
    error: null,
  };
}

function items(n: number): string {
  return n === 1 ? '1 item' : `${n} items`;
}

/** Mockup 02's banner, in two parts because the first is bold. */
export function unitFixBanner(n: number): { lead: string; rest: string } {
  return {
    lead: `${items(n)} could not be imported`,
    rest: ` because ${n === 1 ? 'its' : 'their'} unit is new to Aivor. Choose the unit once and press Apply. Your choices are saved as you go.`,
  };
}

export function applyLabel(n: number): string {
  return n ? `Apply and retry ${items(n)}` : 'Apply';
}

export function appliedToast(n: number): string {
  return `${items(n)} added to Aivor.`;
}
